import React, { useCallback, useLayoutEffect, useState } from 'react';
import cs from 'classnames';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import ClaimStepNavigationWrapper from '../../../../components/ClaimFormComponents/ClaimStepNavigationWrapper';
import Validation from '../../../../components/Validation/Validation';
import MUIDropzone from '../../../../components/MUI/MUIDropzone';
import Loader from '../../../../components/Loaders/Loader';
import {
  prepareFiles,
  validateFiles,
  VALIDATION_ERRORS,
  validator,
} from '../../../../helpers/uploadHelpers';
import { FILE_STATES } from '../../../../constants';

import styles from '../steps.module.scss';
import useStyles from '../../useStyles';

const AttachmentsStep = ({
  initialData,
  setStepNavigationBox,
  loadingProgress,
  isDeviceLgSize,
  goPrevious,
  options,
  attachments,
  step,
  goNext,
  attachmentConstraints,
  getValidationRules,
  uploadActions,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const dispatch = useDispatch();

  const [errors, setErrors] = useState({});
  const [showErrors, setShowErrors] = useState(false);

  useLayoutEffect(() => {
    const validationRules = getValidationRules();
    setErrors(validator(attachments, validationRules));
  }, [attachments, getValidationRules]);

  const shouldShowErrors = (fileType) => showErrors
    || (errors[fileType] && errors[fileType].type === VALIDATION_ERRORS.MAX_NUMBER_EXCEEDED.type);

  const handleInvalidFile = (file, error) => {
    dispatch(uploadActions.setFileState(file.src, FILE_STATES.INVALID));
    dispatch(uploadActions.setFileError(file.src, error));
  };

  const handleAddFiles = (fileList, filetype) => {
    const files = prepareFiles(fileList, filetype);
    validateFiles(files, attachmentConstraints, handleInvalidFile);
    dispatch(uploadActions.addFiles(files));
  };

  const handleRemoveFile = useCallback(
    (fileSrc) => {
      URL.revokeObjectURL(fileSrc);
      dispatch(uploadActions.removeFile(fileSrc));
    },
    [dispatch, uploadActions],
  );

  const handleSubmit = () => () => {
    const errorsExist = Object.values(errors).some((e) => !!e);
    if (errorsExist) {
      setShowErrors(true);
      return;
    }

    dispatch(uploadActions.uploadFiles(attachments))
      .then(() => {
        goNext(step);
      })
      .catch(() => {});
  };

  return (
    <div className={styles.formContainer}>
      {initialData.isUploading && <Loader progress={loadingProgress} />}
      <ClaimStepNavigationWrapper
        clearError={() => {
        }}
        errors={errors}
        isNextDisabled={false}
        handleSubmit={handleSubmit}
        setStepNavigationBox={setStepNavigationBox}
        isDeviceLgSize={isDeviceLgSize}
        onSubmit={() => {
        }}
        back={goPrevious}
        dependencies={[JSON.stringify(errors), isDeviceLgSize, false, initialData.files]}
      />
      <div className={classes.rowTitle}>{t('claim.attachmentInfo')}</div>
      <div className={cs(styles.formRow, styles.fileInfo)}>
        <div
          dangerouslySetInnerHTML={{
            __html: t('claim.attachments.fileSizeInfo')
              .replace(/<strong>/g, '<strong style="color: black;">')
              .replace(/<\/strong>/g, '</strong>'),
          }}
        />
        <div
          dangerouslySetInnerHTML={{
            __html: t('claim.attachments.fileTypeInfo')
              .replace(/<strong>/g, '<strong style="color: black;">')
              .replace(/<\/strong>/g, '</strong>'),
          }}
        />
        <div
          dangerouslySetInnerHTML={{
            __html: t('claim.attachments.fileNumberInfo')
              .replace(/<strong>/g, '<strong style="color: black;">')
              .replace(/<\/strong>/g, '</strong>'),
          }}
        />
      </div>
      {
        options.map((option) => {
          if (!isEmpty(option)) {
            return (
              <React.Fragment key={option.id}>
                {
                      option.hasSectionTitle && (
                      <div className={cs(classes.rowTitle, classes.attachmentTitle)}>
                        {option.sectionTitle}
                      </div>
                      )
                  }
                <div className={cs(styles.formRow)}>
                  <Validation
                    name={option.name}
                    errors={errors}
                    showMessage={shouldShowErrors(option.name)}
                  >
                    <MUIDropzone
                      files={initialData.files.filter((f) => f.type === option.name)}
                      title={option.title}
                      description={option.description}
                      icon={option.icon}
                      fileData={initialData}
                      handleAddFiles={(files) => handleAddFiles(files, option.name)}
                      handleRemoveFile={handleRemoveFile}
                      accept={option.accept}
                    />
                  </Validation>
                </div>
              </React.Fragment>
            );
          }
          return null;
        })
      }
    </div>
  );
};

AttachmentsStep.propTypes = {
  initialData: PropTypes.shape({
    isUploading: PropTypes.bool,
    files: PropTypes.arrayOf(
      PropTypes.shape({
        isImage: PropTypes.bool.isRequired,
      }),
    ).isRequired,
  }).isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      icon: PropTypes.string,
      accept: PropTypes.string,
      hasSectionTitle: PropTypes.bool,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    }),
  ).isRequired,
  setStepNavigationBox: PropTypes.func.isRequired,
  goPrevious: PropTypes.func.isRequired,
  loadingProgress: PropTypes.bool.isRequired,
  isDeviceLgSize: PropTypes.bool.isRequired,
  step: PropTypes.shape({
    n: PropTypes.number.isRequired,
  }).isRequired,
  goNext: PropTypes.func.isRequired,
  uploadActions: PropTypes.shape({
    setFileState: PropTypes.func.isRequired,
    setFileError: PropTypes.func.isRequired,
    removeFile: PropTypes.func.isRequired,
    uploadFiles: PropTypes.func.isRequired,
    addFiles: PropTypes.func.isRequired,
  }).isRequired,
  attachmentConstraints: PropTypes.shape({}).isRequired,
  attachments: PropTypes.shape({}).isRequired,
  getValidationRules: PropTypes.func.isRequired,
};

export default AttachmentsStep;
